import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./RobotPlanningCalendar.css";
import { generateClientId } from "../../utils/clientId";
import { CONTROLE_BIS_DOSSIER_TYPE } from "../controle/shared/controleConstants";
import { PATIENT_VALIDATION_KEY } from "../controle/shared/patientControlRules";
import {
    API,
    createControleDossierLot,
    createRobotPlanningProduction,
    deleteRobotPlanningProduction,
    getControleDossiersLots,
    getRobotPlanningProductions,
    updateControleDossierLot,
    updateRobotPlanningProduction,
} from "../../services/api";

const MONTH_LABELS = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
];

const WEEK_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
// En vue mois, au-delà de ce nombre, les productions du jour sont résumées par « +N autres ».
const MONTH_VISIBLE_EVENTS = 3;
const STORAGE_KEY = "robot-planning-productions";
const DEFAULT_PRODUCTION_COLOR = "#DC2626";
const PRODUCTION_STATUS_STYLES = {
    NOT_STARTED: {
        label: "Rien de fait",
        background: "#fee2e2",
        border: "#fecaca",
        color: "#991b1b",
    },
    IN_PROGRESS: {
        label: "Production en cours",
        background: "#ffedd5",
        border: "#fed7aa",
        color: "#9a3412",
    },
    DONE: {
        label: "Tout est fait",
        background: "#dcfce7",
        border: "#86efac",
        color: "#166534",
    },
};
const FREQUENCY_OPTIONS = [
    { value: "HEBDOMADAIRE", label: "Hebdomadaire", days: 7 },
    { value: "TOUTES_LES_2_SEMAINES", label: "Toutes les 2 semaines", days: 14 },
    { value: "MENSUELLE", label: "Mensuelle", months: 1 },
    { value: "PONCTUELLE", label: "Ponctuelle" },
];

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfWeek(date) {
    const weekStart = new Date(date);
    const dayIndex = (weekStart.getDay() + 6) % 7;
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(weekStart.getDate() - dayIndex);
    return weekStart;
}

function addMonths(date, amount) {
    return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function addWeeks(date, amount) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + amount * 7);
    return nextDate;
}

function sameDate(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

function buildCalendarDays(monthDate) {
    const monthStart = startOfMonth(monthDate);
    const firstDayIndex = (monthStart.getDay() + 6) % 7;
    const gridStart = new Date(monthStart);
    gridStart.setDate(monthStart.getDate() - firstDayIndex);

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + index);
        return date;
    });
}

function buildWeekDays(date) {
    const weekStart = startOfWeek(date);
    return Array.from({ length: 7 }, (_, index) => {
        const weekDate = new Date(weekStart);
        weekDate.setDate(weekStart.getDate() + index);
        return weekDate;
    });
}

function formatMonthTitle(date) {
    return `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;
}

function formatWeekTitle(date) {
    const weekDays = buildWeekDays(date);
    const firstDay = weekDays[0];
    const lastDay = weekDays[6];
    const firstLabel = `${firstDay.getDate()} ${MONTH_LABELS[firstDay.getMonth()]}`;
    const lastLabel = `${lastDay.getDate()} ${MONTH_LABELS[lastDay.getMonth()]}`;
    const yearLabel = firstDay.getFullYear() === lastDay.getFullYear()
        ? lastDay.getFullYear()
        : `${firstDay.getFullYear()} - ${lastDay.getFullYear()}`;

    return `Semaine du ${firstLabel} au ${lastLabel} ${yearLabel}`;
}

function dateKey(date) {
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ].join("-");
}

function parseDateKey(value) {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

function addDaysToDateKey(value, days) {
    const date = parseDateKey(value);
    if (!date) return null;
    date.setDate(date.getDate() + days);
    return dateKey(date);
}

function formatDateLabel(value) {
    if (!value) return "";
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
}

function productionDisplayName(production) {
    return production?.ehpadSubcategory || production?.ehpadName || "EHPAD";
}

function isControleDossier(dossier) {
    return dossier?.donnees?.meta?.dossierType === CONTROLE_BIS_DOSSIER_TYPE;
}

function sameProductionDossier(dossier, event, productionDate) {
    return (
        sameProductionDossierIdentity(dossier, event, productionDate)
        && dossier.dateProduction === productionDate
    );
}

function sameProductionDossierIdentity(dossier, event, productionDate) {
    const meta = dossier?.donnees?.meta || {};
    return (
        isControleDossier(dossier)
        && String(dossier.ehpadId) === String(event.ehpadId)
        && String(meta.robotPlanningProductionId || "") === String(event.id)
        && String(meta.robotPlanningOriginalDate || "") === String(event.originalEventDate || productionDate)
    );
}

function productionDossierKey(ehpadId, productionId, originalDate, productionDate) {
    return [
        String(ehpadId || ""),
        String(productionId || ""),
        String(originalDate || productionDate || ""),
        String(productionDate || ""),
    ].join("|");
}

function numericValue(value) {
    const parsed = Number(String(value ?? "").replace(",", "."));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function productionStatus(total, validated) {
    if (total > 0 && validated >= total) return "DONE";
    if (validated > 0) return "IN_PROGRESS";
    return "NOT_STARTED";
}

function productionDossierProgress(dossier) {
    if (!dossier) {
        return {
            status: "NOT_STARTED",
            label: PRODUCTION_STATUS_STYLES.NOT_STARTED.label,
            validated: 0,
            total: 0,
            remaining: 0,
            hasDossier: false,
        };
    }

    const data = dossier.donnees || {};
    const controls = data.controlePatients || {};
    const countedValidated = Object.values(controls).filter(patient => Boolean(patient?.[PATIENT_VALIDATION_KEY])).length;
    const declaredTotal = Math.max(
        numericValue(data.meta?.nombrePatients),
        numericValue(data.meta?.patientTotal),
        numericValue(data.meta?.robotPlanningPatientTotal),
    );
    const total = Math.max(declaredTotal, Object.keys(controls).length);
    const validated = Math.min(total || countedValidated, Math.max(countedValidated, numericValue(data.meta?.robotPlanningPatientValidated)));
    const remaining = Math.max(total - validated, 0);
    const status = productionStatus(total, validated);

    return {
        status,
        label: PRODUCTION_STATUS_STYLES[status].label,
        validated,
        total,
        remaining,
        hasDossier: true,
    };
}

function productionProgressLabel(progress) {
    const safeProgress = progress || productionDossierProgress(null);
    const patientLabel = safeProgress.total > 0
        ? `${safeProgress.validated}/${safeProgress.total} patients validés`
        : "0 patient validé";
    return `${patientLabel} · ${safeProgress.label}`;
}

function productionDossierData(dossier, event, productionDate, deliveryDate, mebDate) {
    const data = dossier?.donnees && typeof dossier.donnees === "object" ? dossier.donnees : {};
    return {
        ...data,
        meta: {
            ...data.meta,
            dossierType: CONTROLE_BIS_DOSSIER_TYPE,
            robotPlanningProductionId: event.id,
            robotPlanningOriginalDate: event.originalEventDate || productionDate,
            robotPlanningEventDate: productionDate,
            ehpad: event.ehpadName || "",
            productionSubcategory: event.ehpadSubcategory || "",
            dateProduction: productionDate,
            dateLivraison: deliveryDate || "",
            dateMeb: mebDate || "",
        },
    };
}

function legacyFrequencyDays(value) {
    const option = FREQUENCY_OPTIONS.find(item => item.value === value);
    if (!option || option.value === "PONCTUELLE") return 0;
    if (option.days) return option.days;
    if (option.months) return 28 * option.months;
    return 7;
}

function productionFrequencyDays(production) {
    const parsed = Number(production?.frequencyDays);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    return legacyFrequencyDays(production?.frequency) || 7;
}

function productionMebOffsetDays(production) {
    const parsed = Number(production?.mebOffsetDays);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    return productionFrequencyDays(production);
}

function readOccurrenceOverrides(production) {
    const raw = production?.occurrenceOverridesJson;
    if (!raw) return {};
    if (typeof raw === "object") return raw;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

function occurrenceDate(production, originalDate) {
    const override = readOccurrenceOverrides(production)[originalDate];
    if (!override) return originalDate;
    return typeof override === "string" ? override : (override.productionDate || originalDate);
}

function occurrenceDetail(production, originalDate) {
    const productionDate = occurrenceDate(production, originalDate);
    const override = readOccurrenceOverrides(production)[originalDate];
    const detail = typeof override === "object" && override ? override : {};
    return {
        productionDate,
        deliveryDate: detail.deliveryDate || addDaysToDateKey(productionDate, 1),
        mebDate: detail.mebDate || addDaysToDateKey(productionDate, productionMebOffsetDays(production)),
    };
}

function productionPayload(production) {
    return {
        ehpadId: Number(production.ehpadId),
        ehpadName: production.ehpadName,
        ehpadSubcategory: production.ehpadSubcategory || null,
        firstProductionDate: production.firstProductionDate,
        frequency: production.frequency,
        frequencyDays: productionFrequencyDays(production),
        mebDate: production.mebDate,
        mebOffsetDays: productionMebOffsetDays(production),
        color: DEFAULT_PRODUCTION_COLOR,
        occurrenceOverridesJson: production.occurrenceOverridesJson || "",
    };
}

function nextProductionDate(production, fromDate = new Date()) {
    const firstDate = production.firstProductionDate;
    const frequencyDays = productionFrequencyDays(production);
    if (!firstDate || frequencyDays < 1) return null;

    let nextDate = addDaysToDateKey(firstDate, frequencyDays);
    const minDate = dateKey(fromDate);
    let guard = 0;
    while (nextDate && nextDate < minDate && guard < 260) {
        nextDate = addDaysToDateKey(nextDate, frequencyDays);
        guard += 1;
    }
    return nextDate;
}

// Ancienne copie du planning gardée dans le navigateur : le serveur fait foi, elle est seulement effacée du poste.
function clearLegacyStoredProductions() {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

export default function RobotPlanningCalendar({ headerTabs = null }) {
    const navigate = useNavigate();
    const [visibleMonth, setVisibleMonth] = useState(() => new Date());
    const [calendarView, setCalendarView] = useState("week");
    const [ehpads, setEhpads] = useState([]);
    const [productions, setProductions] = useState([]);
    const [controleDossiers, setControleDossiers] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showListModal, setShowListModal] = useState(false);
    const [editingProduction, setEditingProduction] = useState(null);
    const [deletingProduction, setDeletingProduction] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [draggedEvent, setDraggedEvent] = useState(null);
    const [dragOverDate, setDragOverDate] = useState("");
    const [detailDate, setDetailDate] = useState("");
    const [detailDeliveryDate, setDetailDeliveryDate] = useState("");
    const [detailMebDate, setDetailMebDate] = useState("");
    const [detailSaving, setDetailSaving] = useState(false);
    const [detailOpeningDossier, setDetailOpeningDossier] = useState(false);
    const [detailMessage, setDetailMessage] = useState("");
    const [planningError, setPlanningError] = useState(null);
    const today = useMemo(() => new Date(), []);
    const days = useMemo(
        () => (calendarView === "week" ? buildWeekDays(visibleMonth) : buildCalendarDays(visibleMonth)),
        [calendarView, visibleMonth],
    );
    const calendarTitle = calendarView === "week" ? formatWeekTitle(visibleMonth) : formatMonthTitle(visibleMonth);
    const navigationUnitLabel = calendarView === "week" ? "Semaine" : "Mois";
    const controleDossiersByProduction = useMemo(() => {
        const map = new Map();
        controleDossiers.filter(isControleDossier).forEach(dossier => {
            const meta = dossier?.donnees?.meta || {};
            const key = productionDossierKey(
                dossier.ehpadId,
                meta.robotPlanningProductionId,
                meta.robotPlanningOriginalDate || dossier.dateProduction,
                dossier.dateProduction,
            );
            map.set(key, dossier);
        });
        return map;
    }, [controleDossiers]);
    const productionsByDate = useMemo(() => {
        const map = new Map();
        const visibleDates = days.map(dateKey);
        const rangeStart = visibleDates[0];
        const rangeEnd = visibleDates[visibleDates.length - 1];
        const addOccurrence = (production, originalDate) => {
            const date = occurrenceDate(production, originalDate);
            const dossier = controleDossiersByProduction.get(productionDossierKey(
                production.ehpadId,
                production.id,
                originalDate,
                date,
            ));
            const progress = productionDossierProgress(dossier);
            if (!map.has(date)) map.set(date, []);
            map.get(date).push({
                ...production,
                eventDate: date,
                originalEventDate: originalDate,
                eventType: "Production",
                editableOccurrence: true,
                controleDossier: dossier || null,
                dossierProgress: progress,
            });
        };
        productions.forEach(production => {
            const firstDate = production.firstProductionDate;
            const frequencyDays = productionFrequencyDays(production);
            if (!firstDate || !rangeStart || !rangeEnd) return;

            let originalDate = firstDate;
            if (frequencyDays < 1) {
                if (originalDate >= rangeStart && originalDate <= rangeEnd) {
                    addOccurrence(production, originalDate);
                }
                return;
            }
            let guard = 0;
            while (originalDate && originalDate < rangeStart && guard < 520) {
                originalDate = addDaysToDateKey(originalDate, frequencyDays);
                guard += 1;
            }

            while (originalDate && originalDate <= rangeEnd && guard < 620) {
                addOccurrence(production, originalDate);
                originalDate = addDaysToDateKey(originalDate, frequencyDays);
                guard += 1;
            }
        });
        return map;
    }, [controleDossiersByProduction, days, productions]);

    useEffect(() => {
        clearLegacyStoredProductions();
        API.get("/ehpads", { noRedirectOn401: true })
            .then(({ data }) => setEhpads(data || []))
            .catch(() => setEhpads([]));
        getRobotPlanningProductions()
            .then(({ data }) => {
                setProductions(data || []);
                setPlanningError(null);
            })
            .catch(() => {
                setPlanningError("Planning non chargé : API indisponible.");
            });
        getControleDossiersLots()
            .then(({ data }) => setControleDossiers(data || []))
            .catch(() => setControleDossiers([]));
    }, []);

    const addProduction = async (production) => {
        try {
            const { data } = await createRobotPlanningProduction(production);
            setProductions(prev => [...prev, data]);
            setPlanningError(null);
            setShowAddModal(false);
        } catch {
            const localProduction = {
                ...production,
                id: generateClientId("robot-production"),
                createdAt: new Date().toISOString(),
            };
            setProductions(prev => [...prev, localProduction]);
            setPlanningError("Production affichée sans être enregistrée (perdue au rechargement) : API indisponible.");
            setShowAddModal(false);
        }
    };

    const editProduction = async (production) => {
        const nextProduction = {
            ...production,
            occurrenceOverridesJson: production.occurrenceOverridesJson ?? editingProduction?.occurrenceOverridesJson ?? "",
        };
        try {
            const { data } = await updateRobotPlanningProduction(editingProduction.id, nextProduction);
            setProductions(prev => prev.map(item => item.id === editingProduction.id ? data : item));
            setPlanningError(null);
            setEditingProduction(null);
        } catch {
            const localProduction = { ...editingProduction, ...nextProduction };
            setProductions(prev => prev.map(item => item.id === editingProduction.id ? localProduction : item));
            setPlanningError("Modification affichée sans être enregistrée (perdue au rechargement) : API indisponible.");
            setEditingProduction(null);
        }
    };

    const selectCalendarEvent = (event) => {
        const detail = occurrenceDetail(event, event.originalEventDate);
        setSelectedEvent(event);
        setDetailDate(detail.productionDate || event.eventDate || event.originalEventDate || "");
        setDetailDeliveryDate(detail.deliveryDate || "");
        setDetailMebDate(detail.mebDate || "");
        setDetailMessage("");
    };

    const persistProduction = async (production, successMessage, localMessage) => {
        try {
            const { data } = await updateRobotPlanningProduction(production.id, productionPayload(production));
            setProductions(prev => prev.map(item => item.id === production.id ? data : item));
            setSelectedEvent(current => current?.id === production.id
                ? {
                    ...current,
                    ...data,
                    eventDate: occurrenceDate(data, current.originalEventDate),
                }
                : current
            );
            setPlanningError(null);
            setDetailMessage(successMessage);
        } catch {
            setProductions(prev => prev.map(item => item.id === production.id ? production : item));
            setSelectedEvent(current => current?.id === production.id
                ? {
                    ...current,
                    ...production,
                    eventDate: occurrenceDate(production, current.originalEventDate),
                }
                : current
            );
            setPlanningError(localMessage);
            setDetailMessage(localMessage);
        }
    };

    const moveOccurrence = async (event, productionDate, deliveryDate = "", mebDate = "") => {
        if (!event || !productionDate || !event.editableOccurrence) return;
        const sourceProduction = productions.find(item => item.id === event.id) || event;
        const overrides = readOccurrenceOverrides(sourceProduction);
        const defaultDeliveryDate = addDaysToDateKey(productionDate, 1);
        const defaultMebDate = addDaysToDateKey(productionDate, productionMebOffsetDays(sourceProduction));
        const nextOverride = {
            productionDate,
            deliveryDate: deliveryDate || defaultDeliveryDate,
            mebDate: mebDate || defaultMebDate,
        };
        const isDefault =
            nextOverride.productionDate === event.originalEventDate &&
            nextOverride.deliveryDate === addDaysToDateKey(event.originalEventDate, 1) &&
            nextOverride.mebDate === addDaysToDateKey(event.originalEventDate, productionMebOffsetDays(sourceProduction));
        if (isDefault) delete overrides[event.originalEventDate];
        else overrides[event.originalEventDate] = nextOverride;
        const updatedProduction = {
            ...sourceProduction,
            occurrenceOverridesJson: Object.keys(overrides).length ? JSON.stringify(overrides) : "",
        };
        await persistProduction(
            updatedProduction,
            "Occurrence déplacée.",
            "Déplacement affiché sans être enregistré (perdu au rechargement) : API indisponible."
        );
        await migrateProductionControleDossier(event, nextOverride.productionDate, nextOverride.deliveryDate, nextOverride.mebDate);
        if (selectedEvent?.id === event.id && selectedEvent?.originalEventDate === event.originalEventDate) {
            setDetailDate(nextOverride.productionDate);
            setDetailDeliveryDate(nextOverride.deliveryDate || "");
            setDetailMebDate(nextOverride.mebDate || "");
        }
    };

    const saveOccurrenceDate = async () => {
        if (!selectedEvent || !detailDate || !selectedEvent.editableOccurrence) return;
        setDetailSaving(true);
        await moveOccurrence(selectedEvent, detailDate, detailDeliveryDate, detailMebDate);
        setDetailSaving(false);
    };

    const startDragEvent = (event, calendarEvent) => {
        setDraggedEvent(calendarEvent);
        setDetailMessage("");
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/json", JSON.stringify({
            id: calendarEvent.id,
            originalEventDate: calendarEvent.originalEventDate,
        }));
    };

    const dropCalendarEvent = async (targetDate) => {
        const sourceEvent = draggedEvent;
        setDraggedEvent(null);
        setDragOverDate("");
        if (!sourceEvent || !targetDate || sourceEvent.eventDate === targetDate) return;
        await moveOccurrence(sourceEvent, targetDate);
    };

    const updateLocalControleDossier = (dossier) => {
        setControleDossiers(prev => {
            const others = prev.filter(item => item.id !== dossier.id);
            return [...others, dossier];
        });
        setSelectedEvent(current => {
            if (!current || !sameProductionDossierIdentity(dossier, current, current.eventDate)) return current;
            const progress = productionDossierProgress(dossier);
            return {
                ...current,
                controleDossier: dossier,
                dossierProgress: progress,
            };
        });
    };

    const migrateProductionControleDossier = async (event, productionDate, deliveryDate, mebDate) => {
        const dossier = event?.controleDossier;
        if (!dossier || !productionDate) return;
        const donnees = productionDossierData(dossier, event, productionDate, deliveryDate, mebDate);
        try {
            const { data } = await updateControleDossierLot(dossier.id, {
                status: dossier.status || "BROUILLON",
                dateProduction: productionDate,
                donnees,
            });
            updateLocalControleDossier(data);
        } catch {
            setDetailMessage("Occurrence déplacée, mais migration du dossier de contrôle impossible.");
        }
    };

    const openProductionControleDossier = async () => {
        if (!selectedEvent) return;
        const productionDate = detailDate || selectedEvent.eventDate;
        if (!productionDate) return;
        setDetailOpeningDossier(true);
        setDetailMessage("");
        try {
            const { data: existingDossiers } = await getControleDossiersLots({
                ehpadId: Number(selectedEvent.ehpadId),
                dateDebut: productionDate,
                dateFin: productionDate,
            });
            const dossier = (existingDossiers || []).find(item => sameProductionDossier(item, selectedEvent, productionDate))
                || controleDossiers.find(item => sameProductionDossierIdentity(item, selectedEvent, productionDate));
            let activeDossier = dossier;
            if (!activeDossier) {
                activeDossier = (await createControleDossierLot(Number(selectedEvent.ehpadId), productionDate, CONTROLE_BIS_DOSSIER_TYPE)).data;
                activeDossier = (await updateControleDossierLot(activeDossier.id, {
                    status: activeDossier.status || "BROUILLON",
                    dateProduction: productionDate,
                    donnees: productionDossierData(activeDossier, selectedEvent, productionDate, detailDeliveryDate, detailMebDate),
                })).data;
            } else if (activeDossier.dateProduction !== productionDate) {
                activeDossier = (await updateControleDossierLot(activeDossier.id, {
                    status: activeDossier.status || "BROUILLON",
                    dateProduction: productionDate,
                    donnees: productionDossierData(activeDossier, selectedEvent, productionDate, detailDeliveryDate, detailMebDate),
                })).data;
            }
            updateLocalControleDossier(activeDossier);

            navigate(`/controle-bis?dossierId=${activeDossier.id}&tab=controlePatient`);
        } catch {
            setDetailMessage("Ouverture du dossier de contrôle impossible.");
        } finally {
            setDetailOpeningDossier(false);
        }
    };

    const updateDetailProductionDate = (value) => {
        setDetailDate(value);
        if (!selectedEvent || !value) return;
        setDetailDeliveryDate(addDaysToDateKey(value, 1) || "");
        setDetailMebDate(addDaysToDateKey(value, productionMebOffsetDays(selectedEvent)) || "");
    };

    const confirmDeleteProduction = async () => {
        if (!deletingProduction) return;
        const deletedId = deletingProduction.id;
        try {
            await deleteRobotPlanningProduction(deletedId);
            setPlanningError(null);
        } catch {
            setPlanningError("Suppression affichée sans être enregistrée (annulée au rechargement) : API indisponible.");
        } finally {
            setProductions(prev => prev.filter(item => item.id !== deletedId));
            setDeletingProduction(null);
        }
    };

    return (
        <div className="robot-planning" style={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            background: "white",
        }}>
            <div className="robot-planning__header" style={{
                padding: "14px 20px 12px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
                flexShrink: 0,
            }}>
                <span className="robot-planning__eyebrow">Planning de production</span>
                <div className="robot-planning__title-wrap">
                    <span className="robot-planning__mark" aria-hidden="true">✓</span>
                    <h2 className="robot-planning__title" style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#0f172a" }}>
                        Planning robot
                    </h2>
                    <span className="robot-planning__count">{productions.length}</span>
                </div>
                {headerTabs}
                <span className="robot-planning__badge" style={{
                    background: "#f8fafc",
                    color: "#64748b",
                    border: "1px solid #e2e8f0",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "2px 9px",
                }}>
                    Calendrier
                </span>
                {planningError && (
                    <span style={{
                        background: "#fffbeb",
                        color: "#92400e",
                        border: "1px solid #fde68a",
                        borderRadius: "999px",
                        fontSize: "12px",
                        fontWeight: 700,
                        padding: "2px 9px",
                    }}>
                        {planningError}
                    </span>
                )}
                <div className="robot-planning__spacer" style={{ flex: 1 }} />
                <button
                    type="button"
                    onClick={() => setShowAddModal(true)}
                    style={primaryButtonStyle}
                >
                    <span aria-hidden="true">＋</span> Ajouter une production
                </button>
                <button
                    type="button"
                    onClick={() => setShowListModal(true)}
                    style={secondaryButtonStyle}
                >
                    <span aria-hidden="true">☷</span> Voir les productions
                </button>
                <fieldset
                    aria-label="Vue calendrier"
                    className="robot-planning__view-switch"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #e2e8f0",
                        borderRadius: "7px",
                        overflow: "hidden",
                        background: "white",
                    }}
                >
                    <button
                        type="button"
                        aria-pressed={calendarView === "week"}
                        onClick={() => setCalendarView("week")}
                        style={viewToggleButtonStyle(calendarView === "week")}
                    >
                        Semaine
                    </button>
                    <button
                        type="button"
                        aria-pressed={calendarView === "month"}
                        onClick={() => setCalendarView("month")}
                        style={viewToggleButtonStyle(calendarView === "month")}
                    >
                        Mois
                    </button>
                </fieldset>
                <div className="robot-planning__navigation" style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #e2e8f0",
                    borderRadius: "7px",
                    overflow: "hidden",
                    background: "white",
                }}>
                    <button
                        type="button"
                        aria-label={`${navigationUnitLabel} précédente`}
                        onClick={() => setVisibleMonth(prev => (calendarView === "week" ? addWeeks(prev, -1) : addMonths(prev, -1)))}
                        style={navButtonStyle}
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        onClick={() => setVisibleMonth(calendarView === "week" ? new Date() : startOfMonth(new Date()))}
                        style={{ ...navButtonStyle, borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0", minWidth: "92px" }}
                    >
                        Aujourd'hui
                    </button>
                    <button
                        type="button"
                        aria-label={`${navigationUnitLabel} suivante`}
                        onClick={() => setVisibleMonth(prev => (calendarView === "week" ? addWeeks(prev, 1) : addMonths(prev, 1)))}
                        style={navButtonStyle}
                    >
                        ›
                    </button>
                </div>
            </div>

            <div className="robot-planning__body" style={{
                padding: "18px 20px 20px",
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                background: "#f8fafc",
            }}>
                <div className="robot-planning__period" style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "12px",
                }}>
                    <h3 className="robot-planning__period-title" style={{
                        margin: 0,
                        color: "#0f172a",
                        fontSize: "18px",
                        fontWeight: 800,
                        textTransform: "capitalize",
                    }}>
                        {calendarTitle}
                    </h3>
                    <div className="robot-planning__legend" aria-label="Légende des statuts">
                        <span><i className="status-dot status-dot--done" />Tout est fait</span>
                        <span><i className="status-dot status-dot--progress" />En cours</span>
                        <span><i className="status-dot status-dot--todo" />Rien de fait</span>
                    </div>
                </div>

                <div className="robot-planning__layout" style={{
                    display: "grid",
                    gridTemplateColumns: selectedEvent ? "300px minmax(760px, 1fr)" : "minmax(760px, 1fr)",
                    gap: "14px",
                    alignItems: "start",
                }}>
                    {selectedEvent && (
                        <EventDetailPanel
                            event={selectedEvent}
                            date={detailDate}
                            deliveryDate={detailDeliveryDate}
                            mebDate={detailMebDate}
                            saving={detailSaving}
                            message={detailMessage}
                            progress={selectedEvent.dossierProgress}
                            onDateChange={updateDetailProductionDate}
                            onDeliveryDateChange={setDetailDeliveryDate}
                            onMebDateChange={setDetailMebDate}
                            onSaveDate={saveOccurrenceDate}
                            onOpenDossier={openProductionControleDossier}
                            onClose={() => setSelectedEvent(null)}
                            openingDossier={detailOpeningDossier}
                        />
                    )}
                <div className="robot-calendar" style={{
                    minWidth: "760px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    background: "white",
                }}>
                    <div className="robot-calendar__weekdays" style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, minmax(96px, 1fr))",
                        borderBottom: "1px solid #e2e8f0",
                        background: "#f1f5f9",
                    }}>
                        {WEEK_DAYS.map(day => (
                            <div className="robot-calendar__weekday" key={day} style={{
                                padding: "9px 10px",
                                fontSize: "11px",
                                fontWeight: 800,
                                color: "#64748b",
                                textTransform: "uppercase",
                                textAlign: "center",
                            }}>
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="robot-calendar__grid" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(96px, 1fr))" }}>
                        {days.map(date => {
                            const inCurrentRange = calendarView === "week" || date.getMonth() === visibleMonth.getMonth();
                            const isToday = sameDate(date, today);
                            const dayEvents = productionsByDate.get(dateKey(date)) || [];
                            const visibleEvents = calendarView === "month" ? dayEvents.slice(0, MONTH_VISIBLE_EVENTS) : dayEvents;
                            const hiddenEventCount = dayEvents.length - visibleEvents.length;
                            const isDragOver = dragOverDate === dateKey(date);
                            let dayBackground = inCurrentRange ? "white" : "#f8fafc";
                            if (isDragOver) dayBackground = "#fee2e2";
                            let dayNumberColor = inCurrentRange ? "#334155" : "#cbd5e1";
                            if (isToday) dayNumberColor = "white";
                            return (
                                <div
                                    className={`robot-calendar__day${isToday ? " is-today" : ""}${!inCurrentRange ? " is-outside" : ""}${isDragOver ? " is-dragover" : ""}`}
                                    key={date.toISOString()}
                                    aria-label={`Jour ${dateKey(date)}`}
                                    onDragOver={event => {
                                        if (!draggedEvent) return;
                                        event.preventDefault();
                                        event.dataTransfer.dropEffect = "move";
                                        setDragOverDate(dateKey(date));
                                    }}
                                    onDragLeave={() => setDragOverDate(current => current === dateKey(date) ? "" : current)}
                                    onDrop={event => {
                                        event.preventDefault();
                                        dropCalendarEvent(dateKey(date));
                                    }}
                                    style={{
                                        minHeight: calendarView === "week" ? "420px" : "112px",
                                        padding: "8px",
                                        borderRight: date.getDay() === 0 ? "none" : "1px solid #e2e8f0",
                                        borderBottom: "1px solid #e2e8f0",
                                        background: dayBackground,
                                    }}
                                >
                                    <div className="robot-calendar__day-number" style={{
                                        width: "28px",
                                        height: "28px",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginLeft: "auto",
                                        background: isToday ? "#6366f1" : "transparent",
                                        color: dayNumberColor,
                                        fontSize: "12px",
                                        fontWeight: 800,
                                    }}>
                                        {date.getDate()}
                                    </div>
                                    {isToday && <span className="robot-calendar__today-label">Aujourd’hui</span>}
                                    <div
                                        className="robot-calendar__events"
                                        aria-label={`Productions du ${dateKey(date)}`}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "4px",
                                            marginTop: "8px",
                                        }}
                                    >
                                        {visibleEvents.map(event => (
                                            <button
                                                className={`robot-event robot-event--${event?.dossierProgress?.status || "NOT_STARTED"}`}
                                                type="button"
                                                aria-label={productionDisplayName(event)}
                                                key={`${event.id}-${event.eventType}-${event.originalEventDate}`}
                                                draggable={event.editableOccurrence}
                                                title={`${productionDisplayName(event)} - glisser pour déplacer`}
                                                onDragStart={dragEvent => startDragEvent(dragEvent, event)}
                                                onDragEnd={() => {
                                                    setDraggedEvent(null);
                                                    setDragOverDate("");
                                                }}
                                                onClick={() => selectCalendarEvent(event)}
                                                style={{
                                                    width: "100%",
                                                    padding: "5px 6px",
                                                    borderRadius: "5px",
                                                    background: eventBackground(event),
                                                    border: `1px solid ${eventBorder(event)}`,
                                                    color: eventColor(event),
                                                    fontSize: "11px",
                                                    fontWeight: 800,
                                                    overflow: "hidden",
                                                    textAlign: "left",
                                                    cursor: "pointer",
                                                    display: "grid",
                                                    gap: "2px",
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                <span className="robot-event__name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {productionDisplayName(event)}
                                                </span>
                                                <span className="robot-event__status" style={{ fontSize: "10px", fontWeight: 900 }}>
                                                    {productionProgressLabel(event.dossierProgress)}
                                                </span>
                                            </button>
                                        ))}
                                        {hiddenEventCount > 0 && (
                                            <button
                                                type="button"
                                                className="robot-calendar__more"
                                                onClick={() => {
                                                    setVisibleMonth(date);
                                                    setCalendarView("week");
                                                }}
                                            >
                                                +{hiddenEventCount} {hiddenEventCount > 1 ? "autres" : "autre"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                </div>
            </div>

            {showAddModal && (
                <ProductionFormModal
                    title="Ajouter une production"
                    submitLabel="Ajouter"
                    ehpads={ehpads}
                    onClose={() => setShowAddModal(false)}
                    onSubmit={addProduction}
                />
            )}

            {editingProduction && (
                <ProductionFormModal
                    title="Modifier une production"
                    submitLabel="Enregistrer"
                    ehpads={ehpads}
                    production={editingProduction}
                    onClose={() => setEditingProduction(null)}
                    onSubmit={editProduction}
                />
            )}

            {showListModal && (
                <ProductionListModal
                    productions={productions}
                    onClose={() => setShowListModal(false)}
                    onEdit={setEditingProduction}
                    onDelete={setDeletingProduction}
                />
            )}

            {deletingProduction && (
                <DeleteProductionModal
                    production={deletingProduction}
                    onClose={() => setDeletingProduction(null)}
                    onConfirm={confirmDeleteProduction}
                />
            )}
        </div>
    );
}

function ProductionFormModal({ title, submitLabel, ehpads, production, onClose, onSubmit }) {
    const [ehpadId, setEhpadId] = useState(production?.ehpadId ? String(production.ehpadId) : "");
    const [ehpadSubcategory, setEhpadSubcategory] = useState(production?.ehpadSubcategory || "");
    const [firstProductionDate, setFirstProductionDate] = useState(production?.firstProductionDate || "");
    const [frequencyDays, setFrequencyDays] = useState(String(productionFrequencyDays(production || {})));
    const [mebOffsetDays, setMebOffsetDays] = useState(String(productionMebOffsetDays(production || {})));
    const [error, setError] = useState(null);
    const selectedEhpad = ehpads.find(item => String(item.id) === String(ehpadId));
    const subcategories = Array.from(new Set([
        ...(selectedEhpad?.productionSubcategories ?? []),
        ...(production?.ehpadSubcategory ? [production.ehpadSubcategory] : []),
    ]));

    const handleSubmit = () => {
        const parsedFrequencyDays = Number(frequencyDays);
        const parsedMebOffsetDays = Number(mebOffsetDays);
        if (!ehpadId || !firstProductionDate || !parsedFrequencyDays || parsedFrequencyDays < 1 || !Number.isFinite(parsedMebOffsetDays) || parsedMebOffsetDays < 0) {
            setError("Tous les champs sont obligatoires.");
            return;
        }
        onSubmit({
            ehpadId: Number(ehpadId),
            ehpadName: selectedEhpad?.nom || selectedEhpad?.code || "EHPAD",
            ehpadSubcategory: ehpadSubcategory || null,
            firstProductionDate,
            frequency: "HEBDOMADAIRE",
            frequencyDays: parsedFrequencyDays,
            mebDate: addDaysToDateKey(firstProductionDate, parsedMebOffsetDays),
            mebOffsetDays: parsedMebOffsetDays,
            color: DEFAULT_PRODUCTION_COLOR,
            occurrenceOverridesJson: production?.occurrenceOverridesJson || "",
        });
    };

    return (
        <PlanningModal title={title} onClose={onClose}>
            <div className="planning-form" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Field label="EHPAD">
                    <select
                        value={ehpadId}
                        onChange={e => {
                            setEhpadId(e.target.value);
                            setEhpadSubcategory("");
                        }}
                        style={inputStyle}
                    >
                        <option value="">Sélectionner un EHPAD</option>
                        {ehpads.map(ehpad => (
                            <option key={ehpad.id} value={ehpad.id}>{ehpad.nom || ehpad.code}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Sous-catégorie">
                    <select
                        value={ehpadSubcategory}
                        onChange={e => setEhpadSubcategory(e.target.value)}
                        disabled={!ehpadId || subcategories.length === 0}
                        style={{ ...inputStyle, opacity: !ehpadId || subcategories.length === 0 ? 0.65 : 1 }}
                    >
                        <option value="">Aucune sous-catégorie</option>
                        {subcategories.map(subcategory => (
                            <option key={subcategory} value={subcategory}>{subcategory}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Jour de première production">
                    <input type="date" value={firstProductionDate} onChange={e => setFirstProductionDate(e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Fréquence de production (en jours)">
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value={frequencyDays}
                        onChange={e => setFrequencyDays(e.target.value)}
                        style={inputStyle}
                    />
                </Field>
                <Field label="Mise en bouche après (en jours)">
                    <input
                        type="number"
                        min="0"
                        step="1"
                        value={mebOffsetDays}
                        onChange={e => setMebOffsetDays(e.target.value)}
                        style={inputStyle}
                    />
                </Field>
                {error && <div style={{ color: "#dc2626", fontSize: "13px", fontWeight: 700 }}>{error}</div>}
                <div className="planning-form__actions" style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "6px" }}>
                    <button type="button" onClick={onClose} style={secondaryButtonStyle}>Annuler</button>
                    <button type="button" onClick={handleSubmit} style={primaryButtonStyle}>{submitLabel}</button>
                </div>
            </div>
        </PlanningModal>
    );
}

function EventDetailPanel({ event, date, deliveryDate, mebDate, saving, openingDossier, message, progress, onDateChange, onDeliveryDateChange, onMebDateChange, onSaveDate, onOpenDossier, onClose }) {
    const editableProduction = event.editableOccurrence;
    const moved = editableProduction && date && date !== event.originalEventDate;
    const safeProgress = progress || productionDossierProgress(null);
    const progressStyle = PRODUCTION_STATUS_STYLES[safeProgress.status] || PRODUCTION_STATUS_STYLES.NOT_STARTED;
    return (
        <aside className="robot-detail" style={eventDetailPanelStyle}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                <div>
                    <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 900, textTransform: "uppercase" }}>
                        Détail
                    </div>
                    <h3 style={{ margin: "4px 0 0", color: "#0f172a", fontSize: "16px", fontWeight: 900 }}>
                        {productionDisplayName(event)}
                    </h3>
                </div>
                <button type="button" onClick={onClose} aria-label="Fermer le détail" style={closeButtonStyle}>×</button>
            </div>

            <div style={eventDetailMetaStyle}>
                <span>Type</span>
                <strong>{event.eventType}</strong>
                <span>Fréquence</span>
                <strong>Tous les {productionFrequencyDays(event)} jours</strong>
                <span>Date d'origine</span>
                <strong>{formatDateLabel(event.originalEventDate)}</strong>
                <span>MEB par défaut</span>
                <strong>Production + {productionMebOffsetDays(event)} jours</strong>
            </div>

            <div style={{
                display: "grid",
                gap: "5px",
                padding: "10px",
                borderRadius: "8px",
                border: `1px solid ${progressStyle.border}`,
                background: progressStyle.background,
                color: progressStyle.color,
                fontSize: "12px",
                fontWeight: 850,
            }}>
                <strong style={{ fontSize: "13px" }}>{safeProgress.label}</strong>
                <span>{productionProgressLabel(safeProgress)}</span>
                {safeProgress.total > 0 && <span>{safeProgress.remaining} patient{safeProgress.remaining > 1 ? "s" : ""} à faire</span>}
            </div>

            <Field label={editableProduction ? "Date de cette production" : "Date"}>
                <input
                    type="date"
                    value={date || ""}
                    onChange={e => onDateChange(e.target.value)}
                    disabled={!editableProduction || saving}
                    style={inputStyle}
                />
            </Field>
            <Field label="Date de livraison">
                <input
                    type="date"
                    value={deliveryDate || ""}
                    onChange={e => onDeliveryDateChange(e.target.value)}
                    disabled={!editableProduction || saving}
                    style={inputStyle}
                />
            </Field>
            <Field label="Date de mise en bouche (MEB)">
                <input
                    type="date"
                    value={mebDate || ""}
                    onChange={e => onMebDateChange(e.target.value)}
                    disabled={!editableProduction || saving}
                    style={inputStyle}
                />
            </Field>
            {moved && (
                <div style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "7px", padding: "8px 10px", fontSize: "12px", fontWeight: 800, lineHeight: 1.4 }}>
                    Déplacement ponctuel : la récurrence suivante ne sera pas décalée.
                </div>
            )}

            <div style={{ display: "grid", gap: "8px" }}>
                <button
                    type="button"
                    onClick={onSaveDate}
                    disabled={!editableProduction || saving || !date}
                    style={{ ...primaryButtonStyle, justifyContent: "center", opacity: !editableProduction || saving || !date ? 0.6 : 1 }}
                >
                    {saving ? "Enregistrement..." : "Enregistrer la date"}
                </button>
                <button
                    type="button"
                    onClick={onOpenDossier}
                    disabled={!editableProduction || saving || openingDossier || !date}
                    style={{ ...secondaryButtonStyle, justifyContent: "center", opacity: !editableProduction || saving || openingDossier || !date ? 0.6 : 1 }}
                >
                    {openingDossier ? "Ouverture..." : "Consulter la production"}
                </button>
            </div>

            {message && (
                <div style={{ color: message.includes("impossible") ? "#b91c1c" : "#166534", fontSize: "12px", fontWeight: 850 }}>
                    {message}
                </div>
            )}
        </aside>
    );
}

function ProductionListModal({ productions, onClose, onEdit, onDelete }) {
    return (
        <PlanningModal title="Productions existantes" onClose={onClose} width="1060px">
            {productions.length === 0 ? (
                <div style={{ padding: "28px", textAlign: "center", color: "#94a3b8", fontSize: "14px" }}>
                    Aucune production enregistrée.
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="planning-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: "860px" }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>EHPAD</th>
                                <th style={thStyle}>Première production</th>
                                <th style={thStyle}>Fréquence</th>
                                <th style={thStyle}>Prochaine production</th>
                                <th style={thStyle}>MEB</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productions.map(production => (
                                <tr key={production.id}>
                                    <td style={tdStyle}><span className="planning-table__facility"><i>{productionDisplayName(production).slice(0, 2).toUpperCase()}</i>{productionDisplayName(production)}</span></td>
                                    <td style={tdStyle}>{formatDateLabel(production.firstProductionDate)}</td>
                                    <td style={tdStyle}>Tous les {productionFrequencyDays(production)} jours</td>
                                    <td style={tdStyle}>{formatDateLabel(nextProductionDate(production)) || "-"}</td>
                                    <td style={tdStyle}>+{productionMebOffsetDays(production)} jours</td>
                                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                        <button
                                            type="button"
                                            onClick={() => onEdit(production)}
                                            style={smallActionButtonStyle}
                                        >
                                            Modifier
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onDelete(production)}
                                            style={dangerActionButtonStyle}
                                        >
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </PlanningModal>
    );
}

function DeleteProductionModal({ production, onClose, onConfirm }) {
    return (
        <PlanningModal title="Supprimer la production" onClose={onClose} width="440px">
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <p style={{ margin: 0, color: "#334155", fontSize: "14px", lineHeight: 1.45 }}>
                    Confirmer la suppression de la production de {productionDisplayName(production)} du {formatDateLabel(production.firstProductionDate)} ?
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                    <button type="button" onClick={onClose} style={secondaryButtonStyle}>Annuler</button>
                    <button type="button" onClick={onConfirm} style={dangerButtonStyle}>Confirmer la suppression</button>
                </div>
            </div>
        </PlanningModal>
    );
}

function PlanningModal({ title, onClose, width = "520px", children }) {
    return (
        <div className="planning-modal-backdrop" style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.42)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "18px",
        }}>
            <div className="planning-modal" style={{
                width,
                maxWidth: "96vw",
                maxHeight: "90vh",
                overflow: "hidden",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 20px 52px rgba(15, 23, 42, 0.22)",
                display: "flex",
                flexDirection: "column",
            }}>
                <div className="planning-modal__header" style={{
                    padding: "15px 18px",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                }}>
                    <h3 className="planning-modal__title" style={{ margin: 0, color: "#0f172a", fontSize: "15px", fontWeight: 800 }}>{title}</h3>
                    <button type="button" onClick={onClose} aria-label="Fermer" style={closeButtonStyle}>×</button>
                </div>
                <div className="planning-modal__body" style={{ padding: "16px 18px", overflowY: "auto" }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label style={{ display: "block" }}>
            <span style={labelStyle}>{label}</span>
            {children}
        </label>
    );
}

function eventBackground(event) {
    if (event.eventType === "MEB") return "#ecfdf5";
    return (PRODUCTION_STATUS_STYLES[event?.dossierProgress?.status] || PRODUCTION_STATUS_STYLES.NOT_STARTED).background;
}

function eventBorder(event) {
    if (event.eventType === "MEB") return "#bbf7d0";
    return (PRODUCTION_STATUS_STYLES[event?.dossierProgress?.status] || PRODUCTION_STATUS_STYLES.NOT_STARTED).border;
}

function eventColor(event) {
    if (event.eventType === "MEB") return "#15803d";
    return (PRODUCTION_STATUS_STYLES[event?.dossierProgress?.status] || PRODUCTION_STATUS_STYLES.NOT_STARTED).color;
}

const navButtonStyle = {
    height: "36px",
    padding: "0 12px",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "none",
    background: "white",
    color: "#122a26",
    cursor: "pointer",
    fontSize: "12.5px",
    fontWeight: 700,
};

function viewToggleButtonStyle(active) {
    return {
        height: "32px",
        padding: "0 12px",
        borderTop: "none",
        borderRight: "none",
        borderBottom: "none",
        borderLeft: active ? "none" : "1px solid #e2e8f0",
        background: active ? "#ffffff" : "transparent",
        color: active ? "#0c3b3c" : "#5a6d68",
        cursor: "pointer",
        fontSize: "12.5px",
        fontWeight: 800,
    };
}

const primaryButtonStyle = {
    height: "36px",
    padding: "0 13px",
    border: "1px solid #0c3b3c",
    borderRadius: "9px",
    background: "#0c3b3c",
    color: "white",
    cursor: "pointer",
    fontSize: "12.5px",
    fontWeight: 800,
};

const secondaryButtonStyle = {
    height: "36px",
    padding: "0 13px",
    border: "1px solid #e2e8f0",
    borderRadius: "9px",
    background: "white",
    color: "#122a26",
    cursor: "pointer",
    fontSize: "12.5px",
    fontWeight: 800,
};

const dangerButtonStyle = {
    height: "32px",
    padding: "0 13px",
    border: "1px solid #dc2626",
    borderRadius: "7px",
    background: "#dc2626",
    color: "white",
    cursor: "pointer",
    fontSize: "12.5px",
    fontWeight: 800,
};

const smallActionButtonStyle = {
    height: "28px",
    padding: "0 10px",
    marginLeft: "6px",
    border: "1px solid #d6dfdb",
    borderRadius: "6px",
    background: "#ffffff",
    color: "#173f3c",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 800,
};

const dangerActionButtonStyle = {
    ...smallActionButtonStyle,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#b91c1c",
};

const eventDetailPanelStyle = {
    position: "sticky",
    top: 0,
    display: "grid",
    gap: "14px",
    padding: "14px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    background: "white",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
};

const eventDetailMetaStyle = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "4px",
    padding: "10px",
    borderRadius: "8px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#64748b",
    fontSize: "12px",
};

const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    color: "#122a26",
    fontSize: "13px",
    outline: "none",
    background: "white",
    fontFamily: "inherit",
};

const labelStyle = {
    display: "block",
    marginBottom: "5px",
    color: "#5a6d68",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: ".09em",
    textTransform: "uppercase",
};

const closeButtonStyle = {
    width: "28px",
    height: "28px",
    border: "none",
    borderRadius: "6px",
    background: "transparent",
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: "18px",
    lineHeight: 1,
};

const thStyle = {
    padding: "9px 10px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: 800,
    color: "#5a6d68",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
};

const tdStyle = {
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334d49",
    fontSize: "13px",
};
